import React, { useCallback, type ReactNode } from 'react';

import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';

import { usePolarisHeadlessSpotlightMessage } from './usePolarisHeadlessSpotlightMessage.tsx';
import { useDelayedEligibility } from './common/useDelayedEligibility.tsx';
import { useOnboardingFlowsActions } from './controllers/index.tsx';
import { useProductUsageStartTimestamp } from './controllers/selectors/product-usage-start-timestamp-hooks.tsx';
import { useStartWithTemplateSpotlightDismissed } from './controllers/selectors/useStartWithTemplateSpotlightDismissed.tsx';
import { checkIfOlderThanNDays } from './utils/checkIfOlderThanNDays.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';

const PO_PLACEMENT_ID = 'headless';
const PO_MESSAGE_TEMPLATE_ID = 'jpd-headless-spotlight';
const PO_FEATURE_ID = 'start-with-template';

const LEGACY_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS = 6000;
const PO_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS = 3000;

type DismissClickIdentifier = 'dismiss' | 'try-it-now' | 'close';

type UseStartWithTemplateSpotlightParams = {
	isDelayEligible: boolean;
};

type UseStartWithTemplateSpotlightResult = {
	isSpotlightVisible: boolean;
	dismissSpotlight: (clickIdentifier: DismissClickIdentifier) => void;
	renderInPostOfficeMessageInstance: (content: ReactNode) => ReactNode;
	spotlightMessageId: string;
};

const useStartWithTemplateSpotlightWithPo = ({
	isDelayEligible,
}: UseStartWithTemplateSpotlightParams): UseStartWithTemplateSpotlightResult => {
	const isLegacyDismissed = useStartWithTemplateSpotlightDismissed();
	const productUsageStartTimestamp = useProductUsageStartTimestamp();

	const { loadStartWithTemplateSpotlightDismissed, loadProductUsageStartTimestamp } =
		useOnboardingFlowsActions();

	useEffectWithoutConcurrency(() => {
		loadStartWithTemplateSpotlightDismissed();
		loadProductUsageStartTimestamp();
	}, [loadProductUsageStartTimestamp, loadStartWithTemplateSpotlightDismissed]);

	const isNewCustomer =
		Boolean(productUsageStartTimestamp) && !checkIfOlderThanNDays(productUsageStartTimestamp, 7);

	const {
		isPoMessageVisible,
		messageInstanceId,
		messageMetadata,
		PostOfficeMessageInstance,
		dismiss,
	} = usePolarisHeadlessSpotlightMessage({
		placementId: PO_PLACEMENT_ID,
		messageTemplateId: PO_MESSAGE_TEMPLATE_ID,
		featureId: PO_FEATURE_ID,
		legacyDismissed: isLegacyDismissed,
		clickIdentifiers: {
			dismiss: 'close',
			primary: 'try-it-now',
		},
	});

	const isSpotlightEligible =
		isDelayEligible && isLegacyDismissed === false && isNewCustomer && isPoMessageVisible;

	const isSpotlightVisible = useDelayedEligibility({
		isEligible: isSpotlightEligible,
		delayMs: PO_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS,
	});

	const dismissSpotlight = useCallback(
		(clickIdentifier: DismissClickIdentifier) => {
			dismiss(clickIdentifier === 'try-it-now' ? 'primary' : 'dismiss');
		},
		[dismiss],
	);

	const renderInPostOfficeMessageInstance = useCallback(
		(content: ReactNode) => {
			if (PostOfficeMessageInstance && messageInstanceId) {
				return (
					<PostOfficeMessageInstance messageInstanceId={messageInstanceId}>
						{() => content}
					</PostOfficeMessageInstance>
				);
			}

			return content;
		},
		[PostOfficeMessageInstance, messageInstanceId],
	);

	return {
		isSpotlightVisible,
		dismissSpotlight,
		renderInPostOfficeMessageInstance,
		spotlightMessageId: messageMetadata?.messageTemplateId ?? PO_MESSAGE_TEMPLATE_ID,
	};
};

const useStartWithTemplateSpotlightLegacy = ({
	isDelayEligible,
}: UseStartWithTemplateSpotlightParams): UseStartWithTemplateSpotlightResult => {
	const isLegacyDismissed = useStartWithTemplateSpotlightDismissed();
	const productUsageStartTimestamp = useProductUsageStartTimestamp();
	const {
		loadStartWithTemplateSpotlightDismissed,
		loadProductUsageStartTimestamp,
		setStartWithTemplateSpotlightDismissed,
	} = useOnboardingFlowsActions();

	useEffectWithoutConcurrency(() => {
		loadStartWithTemplateSpotlightDismissed();
		loadProductUsageStartTimestamp();
	}, [loadProductUsageStartTimestamp, loadStartWithTemplateSpotlightDismissed]);

	const isNewCustomer =
		Boolean(productUsageStartTimestamp) && !checkIfOlderThanNDays(productUsageStartTimestamp, 7);
	const isSpotlightEligible = isDelayEligible && isLegacyDismissed === false && isNewCustomer;
	const isSpotlightVisible = useDelayedEligibility({
		isEligible: isSpotlightEligible,
		delayMs: LEGACY_START_WITH_TEMPLATE_SPOTLIGHT_DELAY_MS,
	});

	const dismissSpotlight = useCallback(
		(_clickIdentifier: DismissClickIdentifier) => {
			setStartWithTemplateSpotlightDismissed();
		},
		[setStartWithTemplateSpotlightDismissed],
	);

	const renderInPostOfficeMessageInstance = useCallback((content: ReactNode) => content, []);

	return {
		isSpotlightVisible,
		dismissSpotlight,
		renderInPostOfficeMessageInstance,
		spotlightMessageId:
			'lib-onboarding-flows.jira-spotlight.ui.start-with-template-spotlight.jira-spotlight',
	};
};

export const useStartWithTemplateSpotlight: (
	params: UseStartWithTemplateSpotlightParams,
) => UseStartWithTemplateSpotlightResult = functionWithCondition(
	() => fg('post-office_enable_jpd-juno_headless-spotlights'),
	useStartWithTemplateSpotlightWithPo,
	useStartWithTemplateSpotlightLegacy,
);
